// Hooks de datos del Portal de Analítica de Tickets.
// Todo el cálculo vive en las RPCs SECURITY DEFINER get_analitica_* (repo
// sozu-supabase-migrations). El front solo pide y pinta: dashboard y analista IA
// consumen exactamente las mismas RPCs, así que nunca pueden dar números
// distintos para la misma pregunta.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const sb = supabase as any;

export type Ambito = "cliente" | "interno" | "todos";

const dos = (n: number) => String(n).padStart(2, "0");
const isoLocal = (d: Date) => `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
/** Primer día con datos que se pide cuando el periodo es «Todos» (antes no hay tickets). */
const INICIO_HISTORIAL = "2020-01-01";

/**
 * Rango de fechas del periodo de `filtrosAnalitica`: el rango elegido; si no, el año o el mes
 * elegidos (sin pasar de hoy); sin nada elegido, todo el historial hasta hoy.
 */
export function rangoAnalitica(
  periodo: { anio?: number; mes?: number } | null | undefined,
  rango: { desde: string; hasta: string } | null | undefined,
  hoy: Date = new Date(),
): { desde: string; hasta: string } {
  const hoyIso = isoLocal(hoy);
  if (rango) return rango;
  if (!periodo?.anio) return { desde: INICIO_HISTORIAL, hasta: hoyIso };
  const desde = periodo.mes ? `${periodo.anio}-${dos(periodo.mes)}-01` : `${periodo.anio}-01-01`;
  const fin = periodo.mes ? isoLocal(new Date(periodo.anio, periodo.mes, 0)) : `${periodo.anio}-12-31`;
  return { desde, hasta: fin > hoyIso ? hoyIso : fin };
}

export interface Filtros {
  desde: string; // YYYY-MM-DD
  hasta: string; // YYYY-MM-DD
  scope: Ambito;
  pipelines?: number[];
  categorias?: number[];
  prioridades?: string[];
  etapas?: number[];
  responsables?: string[];
  fuentes?: string[];
  incluir_inactivos?: boolean;
}

// Contrato p_filtros jsonb (una sola forma en todo el sistema).
function pFiltros(f: Filtros) {
  return {
    pipelines: f.pipelines ?? [],
    categorias: f.categorias ?? [],
    prioridades: f.prioridades ?? [],
    etapas: f.etapas ?? [],
    responsables: f.responsables ?? [],
    fuentes: f.fuentes ?? [],
    incluir_inactivos: f.incluir_inactivos ?? false,
  };
}

// Detecta la respuesta "dormida" (sin permiso de portal) para pintar un estado claro.
export function sinPermiso(data: any): boolean {
  return !!data && typeof data === "object" && data.error === "sin_permiso";
}

async function rpc(fn: string, args: Record<string, unknown>) {
  const { data, error } = await sb.rpc(fn, args);
  if (error) throw error;
  return data;
}

const base = (f: Filtros) => ({
  p_desde: f.desde,
  p_hasta: f.hasta,
  p_scope: f.scope,
  p_filtros: pFiltros(f),
});

export function useAnaliticaResumen(f: Filtros) {
  return useQuery({
    queryKey: ["analitica-resumen", f],
    queryFn: () => rpc("get_analitica_resumen", base(f)),
    staleTime: 60_000,
  });
}

export function useAnaliticaSerie(f: Filtros, granularidad = "dia", metrica = "creados") {
  return useQuery({
    queryKey: ["analitica-serie", f, granularidad, metrica],
    queryFn: () => rpc("get_analitica_serie", { ...base(f), p_granularidad: granularidad, p_metrica: metrica }),
    staleTime: 60_000,
  });
}

export function useAnaliticaRanking(f: Filtros, dimension = "pipeline", metrica = "volumen", limite = 10) {
  return useQuery({
    queryKey: ["analitica-ranking", f, dimension, metrica, limite],
    queryFn: () => rpc("get_analitica_ranking", { ...base(f), p_dimension: dimension, p_metrica: metrica, p_limite: limite }),
    staleTime: 60_000,
  });
}

export function useAnaliticaDistribucion(f: Filtros, dimension = "categoria") {
  return useQuery({
    queryKey: ["analitica-distribucion", f, dimension],
    queryFn: () => rpc("get_analitica_distribucion", { ...base(f), p_dimension: dimension }),
    staleTime: 60_000,
  });
}

export function useAnaliticaEmbudo(f: Filtros) {
  return useQuery({
    queryKey: ["analitica-embudo", f],
    queryFn: () => rpc("get_analitica_embudo", base(f)),
    staleTime: 60_000,
  });
}

export function useAnaliticaAtencion(f: Filtros) {
  return useQuery({
    queryKey: ["analitica-atencion", f],
    queryFn: () => rpc("get_analitica_atencion", base(f)),
    staleTime: 60_000,
  });
}

export function useAnaliticaRiesgos(f: Filtros) {
  return useQuery({
    queryKey: ["analitica-riesgos", f.scope, f.pipelines, f.categorias],
    queryFn: () => rpc("get_analitica_riesgos", { p_scope: f.scope, p_filtros: pFiltros(f) }),
    staleTime: 60_000,
  });
}

// Drill-down: lista de tickets de un corte (p.ej. un responsable o una categoría).
// `override` mezcla filtros extra sobre p_filtros; se activa solo cuando hay algo que ver.
export function useAnaliticaTickets(f: Filtros, override: Record<string, any> | null) {
  const b = base(f);
  return useQuery({
    queryKey: ["analitica-tickets", f, override],
    queryFn: () => rpc("get_analitica_tickets", { ...b, p_filtros: { ...b.p_filtros, ...(override ?? {}) }, p_limite: 200, p_offset: 0 }),
    enabled: override != null,
    staleTime: 30_000,
  });
}

export function useAnaliticaCatalogos() {
  return useQuery({
    queryKey: ["analitica-catalogos"],
    queryFn: () => rpc("get_analitica_catalogos", {}),
    staleTime: 5 * 60_000,
  });
}

/** Tope de filas que trae un desglose (la RPC entrega de 200 en 200). */
export const TOPE_DESGLOSE = 1000;

/**
 * Desglose de una gráfica (DESIGN §6): todas las filas de `get_analitica_tickets` para el corte,
 * página por página hasta `TOPE_DESGLOSE`. Mismo `base` que las RPC agregadas (periodo por fecha
 * de creación, ámbito y filtros), así el conteo coincide con la barra. `f` puede traer otro
 * periodo (p. ej. un solo día de la tendencia).
 */
export function useAnaliticaTicketsDesglose(f: Filtros, override: Record<string, any> | null) {
  const b = base(f);
  return useQuery({
    queryKey: ["analitica-tickets-desglose", f, override],
    enabled: override != null,
    staleTime: 30_000,
    queryFn: async () => {
      const items: any[] = [];
      let total = 0;
      for (let offset = 0; offset < TOPE_DESGLOSE; offset += 200) {
        const data = await rpc("get_analitica_tickets", {
          ...b,
          p_filtros: { ...b.p_filtros, ...(override ?? {}) },
          p_limite: 200,
          p_offset: offset,
        });
        if (sinPermiso(data)) return { error: "sin_permiso", items: [], total: 0 };
        total = Number(data?.total ?? 0);
        items.push(...((data?.items ?? []) as any[]));
        if (items.length >= total || (data?.items ?? []).length === 0) break;
      }
      return { items, total };
    },
  });
}
